import { Environment } from '@cobliteam/react-environments';
import axios, { type CancelTokenSource } from 'axios';
import { AuthToken } from '../account/session/AuthToken';
import { SessionManager } from '../account/session/SessionManager';
import { CobliAxios } from './CobliAxios';
import type HttpStatusCode from './HttpStatusCode';
import type { Configuration, IRequest } from './Types';

const env = Environment.getInstance().getEnvironment();

class Requester {
  private axiosInstance: CobliAxios;

  private cancelToken: CancelTokenSource | null = null;

  private sessionManager: SessionManager;

  constructor(
    configuration: Configuration,
    sessionManager: SessionManager = new SessionManager({
      host: env.herbieApiHost,
    })
  ) {
    this.axiosInstance = new CobliAxios(configuration);
    this.sessionManager = sessionManager;
  }

  public getCancelToken = () => this.cancelToken;

  public request = (requestData: IRequest, nonReportableToDatadogErrors?: HttpStatusCode[]) => {
    // NOTE: as this token is being shared on a same instance, using the same instance for
    // multiple requests will only allow to cancel the last one.
    this.cancelToken = axios.CancelToken.source();

    return this._request(requestData, this.cancelToken, nonReportableToDatadogErrors);
  };

  public externalRequest = (requestData: IRequest, nonReportableToDatadogErrors?: HttpStatusCode[]) => {
    // NOTE: as this token is being shared on a same instance, using the same instance for
    // multiple requests will only allow to cancel the last one.
    this.cancelToken = axios.CancelToken.source();
    return this._request(requestData, this.cancelToken, nonReportableToDatadogErrors, true);
  };

  public requestWithCancelToken = (requestData: IRequest, cancelToken: CancelTokenSource) => {
    return this._request(requestData, cancelToken);
  };

  private _request = (requestData: IRequest, cancelToken: CancelTokenSource, nonReportableToDatadogErrors: HttpStatusCode[] = [], external = false) => {
    return this.sessionManager.maybeRenewAccessToken().then(() => this.axiosInstance.request(requestData, cancelToken, nonReportableToDatadogErrors, external));
  };

  public getValidToken = async () => {
    const session = await this.sessionManager.maybeRenewAccessToken();
    if (!session) return localStorage.getItem(AuthToken.ACCESS_TOKEN);
    return session.authenticationToken;
  };
}

export default Requester;
